import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Table, DatePicker, Select, message, Popconfirm, Space } from 'antd';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { useExpectedShipments, useShipmentMutations } from '../../hooks/useStudioOps';
import { useGetCompanyListMinimal, fetchCompaniesDetailId } from '../../hooks/useCompanies';

const STATUS = { 1000: 'Draft', 2000: 'Announced', 2100: 'Partially Received', 3000: 'Fully Received', 9000: 'Cancelled' };
const STATUS_OPTIONS = Object.entries(STATUS).map(([value, label]) => ({ value: Number(value), label }));
const EDITABLE = new Set([1000, 2000, 2100]);
const CANCELLABLE = new Set([1000, 2000]);
const PAGE_SIZE = 20;
const defaultEtaRange =() => [dayjs().subtract(7, 'day'), dayjs().add(7, 'day')];

function formatAddress(addr) {
    if (!addr) return '';
    return [addr.alias, addr.addressLine1, addr.addressLine2, addr.city, addr.stateOrProvince, addr.country, addr.zipCode]
        .filter(Boolean)
        .join(', ');
}

function apiError(err, fallback) {
    return err?.response?.data?.message || err?.message || fallback;
}

export default function ExpectedShipments() {
    const [pageNum, setPageNum] = useState(1);
    const [etaRange, setEtaRange] = useState(defaultEtaRange);
    const [statusFilter, setStatusFilter] = useState();
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [form] = Form.useForm();
    const { data, isFetching } = useExpectedShipments({
        pageNum,
        pageSize: PAGE_SIZE,
        status: statusFilter,
        etaFrom: etaRange?.[0] ? etaRange[0].startOf('day').toISOString() : undefined,
        etaTo: etaRange?.[1] ? etaRange[1].endOf('day').toISOString() : undefined,
    });
    const { createExpected, updateExpected, cancelExpected } = useShipmentMutations();
    const { data: companies, isFetching: companiesLoading } = useGetCompanyListMinimal();

    const companyId = selectedCompany?._id;
    const { data: companyDetail, isFetching: addressesLoading } = useQuery(
        ['companyDetail', companyId],
        () => fetchCompaniesDetailId(companyId),
        {
            enabled: !!companyId,
            refetchOnWindowFocus: false,
            select: res => res.data,
        }
    );

    const addresses = useMemo(() => companyDetail?.addresses || [], [companyDetail]);
    const companyOptions = useMemo(
        () =>
            (companies || []).map(company => ({
                label: `${company.code} — ${company.name}`,
                value: company.code,
                company,
            })),
        [companies]
    );
    const addressOptions = useMemo(
        () =>
            addresses.map(addr => ({
                label: formatAddress(addr) || addr.alias || addr._id,
                value: addr._id,
                address: addr,
            })),
        [addresses]
    );

    useEffect(() => {
        if (!editing || !addresses.length) return;
        const current = form.getFieldValue('originAddressId');
        if (current) return;
        const match = addresses.find(addr => formatAddress(addr) === editing.origin);
        if (match) form.setFieldsValue({ originAddressId: match._id });
    }, [editing, addresses, form]);

    const resetForm = () => {
        form.resetFields();
        setSelectedCompany(null);
        setEditing(null);
    };

    const openCreate = () => {
        resetForm();
        setOpen(true);
    };

    const openEdit = record => {
        const company =
            (companies || []).find(c => c._id === record.clientId || c.code === record.companyCode) ||
            (record.clientId ? { _id: record.clientId, code: record.companyCode } : null);
        setEditing(record);
        setSelectedCompany(company);
        form.setFieldsValue({
            companyCode: record.companyCode,
            originAddressId: record.originAddress?._id,
            carrier: record.carrier,
            etaDate: record.etaDate ? dayjs(record.etaDate) : undefined,
            expectedPallets: record.expectedPallets,
            expectedBoxes: record.expectedBoxes,
        });
        setOpen(true);
    };

    const save = async () => {
        const values = await form.validateFields();
        const originAddress = addresses.find(addr => String(addr._id) === String(values.originAddressId));
        const payload = {
            clientId: selectedCompany?._id,
            companyCode: values.companyCode,
            origin: originAddress ? formatAddress(originAddress) : undefined,
            originAddress: originAddress || undefined,
            carrier: values.carrier,
            etaDate: values.etaDate ? values.etaDate.toISOString() : undefined,
            expectedPallets: values.expectedPallets,
            expectedBoxes: values.expectedBoxes,
        };
        try {
            if (editing) {
                await updateExpected.mutateAsync({ id: editing._id, data: payload });
                message.success('Updated');
            } else {
                await createExpected.mutateAsync(payload);
                message.success('Created');
            }
            setOpen(false);
            resetForm();
        } catch (err) {
            message.error(apiError(err, editing ? 'Update failed' : 'Create failed'));
        }
    };

    return (
        <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <DatePicker.RangePicker
                    allowClear
                    value={etaRange}
                    onChange={dates => {
                        setEtaRange(dates);
                        setPageNum(1);
                    }}
                />
                <Select
                    allowClear
                    placeholder="Status"
                    style={{ width: 200 }}
                    options={STATUS_OPTIONS}
                    value={statusFilter}
                    onChange={value => {
                        setStatusFilter(value);
                        setPageNum(1);
                    }}
                />
                <Button type="primary" onClick={openCreate} style={{ marginLeft: 'auto' }}>
                    New expected shipment
                </Button>
            </div>
            <Table
                className="mt-3"
                loading={isFetching}
                rowKey="_id"
                dataSource={data?.data?.data || []}
                pagination={{
                    current: pageNum,
                    pageSize: PAGE_SIZE,
                    total: data?.data?.totalCount,
                    showSizeChanger: false,
                    onChange: setPageNum,
                }}
                columns={[
                    { title: 'Client', dataIndex: 'companyCode' },
                    { title: 'Origin', dataIndex: 'origin' },
                    { title: 'Carrier', dataIndex: 'carrier' },
                    { title: 'ETA', dataIndex: 'etaDate', render: d => (d ? dayjs(d).format('YYYY-MM-DD') : '') },
                    { title: 'Pallets', dataIndex: 'expectedPallets' },
                    { title: 'Boxes', dataIndex: 'expectedBoxes' },
                    { title: 'Status', dataIndex: 'status', render: s => STATUS[s] || s },
                    {
                        title: 'Created by',
                        dataIndex: 'createdBy',
                        render: by => (by && (by.name || by.email)) || '',
                    },
                    {
                        title: '',
                        key: 'actions',
                        width: 160,
                        render: (_, record) => (
                            <Space>
                                <Button type="link" size="small" disabled={!EDITABLE.has(record.status)} onClick={() => openEdit(record)}>
                                    Edit
                                </Button>
                                <Popconfirm
                                    title="Cancel this expected shipment?"
                                    okText="Cancel shipment"
                                    okButtonProps={{ danger: true }}
                                    disabled={!CANCELLABLE.has(record.status) || cancelExpected.isLoading}
                                    onConfirm={async () => {
                                        try {
                                            await cancelExpected.mutateAsync(record._id);
                                            message.success('Cancelled');
                                        } catch (err) {
                                            message.error(apiError(err, 'Cancel failed'));
                                        }
                                    }}
                                >
                                    <Button type="link" size="small" danger disabled={!CANCELLABLE.has(record.status)}>
                                        Cancel
                                    </Button>
                                </Popconfirm>
                            </Space>
                        ),
                    },
                ]}
            />
            <Modal
                open={open}
                width={640}
                title={editing ? 'Edit expected shipment' : 'Expected shipment'}
                okText={editing ? 'Save' : 'Create'}
                confirmLoading={createExpected.isLoading || updateExpected.isLoading}
                onCancel={() => {
                    setOpen(false);
                    resetForm();
                }}
                onOk={save}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="companyCode" label="Client" rules={[{ required: true, message: 'Select a client' }]}>
                        <Select
                            showSearch
                            allowClear
                            loading={companiesLoading}
                            placeholder="Select client"
                            optionFilterProp="label"
                            dropdownMatchSelectWidth={false}
                            options={companyOptions}
                            onChange={(code, option) => {
                                const company = option?.company || null;
                                setSelectedCompany(company);
                                form.setFieldsValue({ originAddressId: undefined });
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="originAddressId"
                        label="Arriving from"
                        rules={[{ required: true, message: 'Select an origin address' }]}
                    >
                        <Select
                            showSearch
                            allowClear
                            disabled={!companyId}
                            loading={addressesLoading}
                            placeholder={companyId ? 'Select origin address' : 'Select a client first'}
                            optionFilterProp="label"
                            dropdownMatchSelectWidth={false}
                            options={addressOptions}
                            notFoundContent={companyId && !addressesLoading && !addresses.length ? 'No addresses on this client' : undefined}
                        />
                    </Form.Item>
                    <Form.Item name="carrier" label="Carrier">
                        <Input />
                    </Form.Item>
                    <Form.Item name="etaDate" label="ETA">
                        <DatePicker />
                    </Form.Item>
                    <Form.Item name="expectedPallets" label="Expected pallets">
                        <InputNumber min={0} />
                    </Form.Item>
                    <Form.Item name="expectedBoxes" label="Expected boxes">
                        <InputNumber min={0} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
